import React, { useCallback, useMemo, useRef, useState } from 'react';

import { hasReportColMentions } from '@aline/common';
import { Box, ButtonBase, InputLabel, Typography, useTheme } from '@mui/material';
import { Formik } from 'formik';
import {
  AlineButton,
  AlineCheckbox,
  AlineTooltip,
  BaseSnackbar,
  ConfirmationDialog,
  FormikAlineInput,
} from 'src/components';
import { useStreamImprovePrompt } from 'src/components/ai/chat/chat-hooks/useImprovePrompt';
import type { AlineMarkdownEditorRef } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor';
import { focusEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/focus';
import { CrossIcon } from 'src/components/icons/CrossIcon';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { Apollo } from 'src/gql/generated';
import {
  useCreateAiReminderAutomation,
  useRunAiReminderAutomation,
  useUpdateAiReminderAutomation,
} from 'src/gql/hooks/aiReminderAutomation';
import { useDispatchCustomSnackFn, useDispatchNotification } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';
import * as Yup from 'yup';

import {
  ReportColumnMentionKit,
  ReportColumnMentionPopover,
  ReportColumnMentionProvider,
  useReportColumnMentionContext,
} from './mention';
import type { NotificationOffset } from './NotificationOffsetBuilder';
import { defaultOffset, NotificationOffsetBuilder } from './NotificationOffsetBuilder';
import type { UserOption } from './UserAutocomplete';
import { UserAutocomplete } from './UserAutocomplete';

const useStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  addContextButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  addContextButtonIcon: {
    alignItems: 'center',
    backgroundColor: theme.appColors.primary,
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    padding: theme.spacing(0.25),
  },
  editorToolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },
  fieldLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  form: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  guidingLabel: {
    color: theme.appColors.secondaryType,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(0.5),
  },
  improvePromptButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(0.5),
    marginLeft: 'auto',
    padding: theme.spacing(0.5, 1.5),
  },
  improvePromptText: {
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 600,
  },
  promptSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: 'auto',
    padding: theme.spacing(1),
  },
  reviewersLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  scrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflow: 'auto',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  titleInput: {
    '& input': {
      fontSize: 20,
      fontWeight: 600,
      padding: theme.spacing(1),
    },
  },
}));

const AutomationSchema = Yup.object().shape({
  prompt: Yup.string()
    .required('Prompt is required')
    .test(
      'has-mentions',
      'Add at least one @mention to provide context for the AI',
      value => !value || hasReportColMentions(value)
    ),
  title: Yup.string().required('Title is required'),
});

interface AIReminderAutomationFormValues {
  assignees: UserOption[];
  deleteRemindersAndRerun: boolean;
  notificationOffsets: NotificationOffset[];
  prompt: string;
  reviewers: UserOption[];
  title: string;
}

interface AIReminderAutomationFormProps {
  onClose: () => void;
  onSaveSuccess: () => void;
  reportId: UUID;
  automation?: Apollo.AiReminderAutomationWithRelationsFragment | null;
}

export function AIReminderAutomationForm(props: AIReminderAutomationFormProps): JSX.Element {
  return (
    <ReportColumnMentionProvider reportId={props.reportId}>
      <AIReminderAutomationFormContent {...props} />
    </ReportColumnMentionProvider>
  );
}

function AIReminderAutomationFormContent({
  automation,
  onClose,
  onSaveSuccess,
  reportId,
}: AIReminderAutomationFormProps): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme();
  const successSnackId = uuid4();
  const isEditing = !!automation;
  const { columns } = useReportColumnMentionContext();
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const { runAutomation } = useRunAiReminderAutomation();
  const [improvePrompt] = Apollo.useImprovePromptMutation();
  const { createAutomation } = useCreateAiReminderAutomation();
  const { updateAutomation } = useUpdateAiReminderAutomation();
  const [mentionPopoverOpen, setMentionPopoverOpen] = useState(false);
  const [promptChangeDialogOpen, setPromptChangeDialogOpen] = useState(false);
  const [improveRequestId, setImproveRequestId] = useState<string | null>(null);
  const [mentionAnchorEl, setMentionAnchorEl] = useState<HTMLElement | null>(null);
  const dispatchSuccessSnack = useDispatchCustomSnackFn({ snackId: successSnackId });
  const dispatchErrorNotification = useDispatchNotification({
    message: 'Something went wrong. Please try again.',
    variant: 'error',
  });
  const [improvePromptState, setImprovePromptState] = useState<'idle' | 'writing'>('idle');

  const handleImprovePromptComplete = useCallback(() => {
    setImproveRequestId(null);
    setImprovePromptState('idle');
    if (editorRef.current) focusEditor(editorRef.current.getEditor(), 'end');
  }, []);

  useStreamImprovePrompt({
    editorRef,
    improveRequestId,
    onComplete: handleImprovePromptComplete,
    onStreamStart: () => setImprovePromptState('writing'),
  });

  const handleImprovePrompt = useCallback(async () => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;

    const markdown = editorRef.current?.getMarkdown?.()?.trim() ?? '';

    const requestId = uuid4();
    setImprovePromptState('writing');
    setImproveRequestId(requestId);

    try {
      await improvePrompt({
        variables: {
          input: {
            availableColumns: columns.map(c => ({ displayName: c.displayName, id: c.id })),
            context: Apollo.ImprovePromptContext.ReportAutomation,
            originalText: markdown,
            requestId,
          },
        },
      });
    } catch {
      setImprovePromptState('idle');
      setImproveRequestId(null);
      dispatchErrorNotification();
    }
  }, [columns, improvePrompt, dispatchErrorNotification]);

  const initialValues: AIReminderAutomationFormValues = useMemo(
    () => ({
      assignees: automation?.assignees ?? [],
      deleteRemindersAndRerun: false,
      notificationOffsets: automation?.notificationOffsets?.map(o => ({
        amount: o.amount,
        position: o.position,
        unit: o.unit,
      })) ?? [{ ...defaultOffset }],
      prompt: automation?.prompt ?? '',
      reviewers: automation?.reviewers ?? [],
      title: automation?.title ?? '',
    }),
    [automation]
  );

  const performSave = useCallback(
    async (values: AIReminderAutomationFormValues) => {
      const assigneeIds = values.assignees.map(a => a.id);
      const reviewerIds = values.reviewers.map(r => r.id);

      try {
        if (isEditing) {
          await updateAutomation({
            variables: {
              input: {
                assigneeIds,
                automationId: automation.id,
                deleteRemindersAndRerun: values.deleteRemindersAndRerun,
                props: {
                  notificationOffsets: values.notificationOffsets,
                  prompt: values.prompt,
                  title: values.title,
                },
                reviewerIds,
              },
            },
          });
        } else {
          const { data: createData } = await createAutomation({
            variables: {
              input: {
                assigneeIds,
                notificationOffsets: values.notificationOffsets,
                prompt: values.prompt,
                reportId,
                reviewerIds,
                title: values.title,
              },
            },
          });
          const newAutomationId = createData?.createAIReminderAutomation?.id;
          if (newAutomationId)
            await runAutomation({ variables: { automationId: newAutomationId } });
        }

        dispatchSuccessSnack(
          <BaseSnackbar id={successSnackId} variant="success">
            <Box>
              <Typography sx={{ fontWeight: 600 }} variant="caption">
                {isEditing ? 'AI reminder updated!' : 'AI reminder created!'}
              </Typography>
              <Typography sx={{ display: 'block' }} variant="caption">
                {isEditing
                  ? 'Your changes have been saved. New reminders will be generated on the next scheduled run.'
                  : `We're generating reminders from this report. You'll receive an email when they're ready to review.`}
              </Typography>
            </Box>
          </BaseSnackbar>
        );
        onSaveSuccess();
      } catch {
        dispatchErrorNotification();
      }
    },
    [
      automation,
      createAutomation,
      dispatchErrorNotification,
      dispatchSuccessSnack,
      isEditing,
      onSaveSuccess,
      reportId,
      runAutomation,
      successSnackId,
      updateAutomation,
    ]
  );

  const handleSubmit = useCallback(
    async (values: AIReminderAutomationFormValues) => {
      const promptChanged = isEditing && values.prompt !== automation.prompt;
      if (promptChanged) {
        setPromptChangeDialogOpen(true);
        return;
      }

      return performSave(values);
    },
    [automation, isEditing, performSave]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={AutomationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, handleReset, handleSubmit, isSubmitting, isValid, setFieldValue, values }) => (
        <form className={classes.form} onReset={handleReset} onSubmit={handleSubmit}>
          <div className={classes.scrollContainer}>
            <FormikAlineInput
              autoFocus
              className={classes.titleInput}
              displayErrorText={false}
              fieldName="title"
              helperText=""
              placeholder="Type a title..."
            />

            <div className={classes.section}>
              <InputLabel className={classes.fieldLabel}>Notifications</InputLabel>
              <NotificationOffsetBuilder
                offsets={values.notificationOffsets}
                onChange={offsets => setFieldValue('notificationOffsets', offsets)}
              />
            </div>

            <div className={classes.section}>
              <InputLabel className={classes.fieldLabel}>Assignees</InputLabel>
              <UserAutocomplete
                placeholder="Select assignees..."
                value={values.assignees}
                onChange={assignees => setFieldValue('assignees', assignees)}
              />
            </div>

            <div className={classes.section}>
              <div className={classes.reviewersLabel}>
                <InputLabel className={classes.fieldLabel}>Reviewers</InputLabel>
                <AlineTooltip
                  arrow
                  placement="top"
                  title="Reviewers will approve or reject AI-generated reminders before they become active"
                >
                  <InfoIcon fill={theme.appColors.iconsType} />
                </AlineTooltip>
              </div>
              <UserAutocomplete
                placeholder="Select reviewers..."
                value={values.reviewers}
                onChange={reviewers => setFieldValue('reviewers', reviewers)}
              />
            </div>
          </div>

          <div className={classes.promptSection}>
            <AlineMarkdownEditor
              ref={editorRef}
              hideToolbar
              bottomContent={
                <div className={classes.editorToolbar}>
                  <AlineTooltip arrow placement="top" title="Add report columns as context">
                    <ButtonBase
                      ref={setMentionAnchorEl}
                      className={classes.addContextButton}
                      onClick={() => setMentionPopoverOpen(true)}
                    >
                      <span className={classes.addContextButtonIcon}>
                        <CrossIcon fill="white" style={{ height: 14, width: 14 }} />
                      </span>
                    </ButtonBase>
                  </AlineTooltip>
                  {editorRef.current && (
                    <ReportColumnMentionPopover
                      anchorEl={mentionAnchorEl}
                      editor={editorRef.current.getEditor()}
                      open={mentionPopoverOpen}
                      onClose={() => setMentionPopoverOpen(false)}
                    />
                  )}
                  <ImprovePromptButton
                    classes={classes}
                    isPromptEmpty={!values.prompt.trim()}
                    state={improvePromptState}
                    onClick={handleImprovePrompt}
                  />
                </div>
              }
              extraPlugins={ReportColumnMentionKit}
              minHeight={120}
              placeholder="Type @ to mention report columns and create a reminder automation..."
              style={{ borderRadius: 12 }}
              value={values.prompt}
              onChange={markdown => setFieldValue('prompt', markdown)}
            />

            <div className={classes.actions}>
              <span style={{ display: 'flex', flex: 1 }}>
                <AlineButton fullWidth color="primary" variant="outlined" onClick={onClose}>
                  Back
                </AlineButton>
              </span>
              <AlineTooltip
                arrow
                placement="top"
                title={
                  !isValid
                    ? (errors.title ?? errors.prompt ?? 'Please fill in all required fields')
                    : ''
                }
              >
                <span style={{ display: 'flex', flex: 1 }}>
                  <AlineButton
                    fullWidth
                    color="primary"
                    disabled={!isValid}
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    {isEditing ? 'Save' : 'Save and Run'}
                  </AlineButton>
                </span>
              </AlineTooltip>
            </div>
          </div>

          <ConfirmationDialog
            TransitionProps={{ onExit: () => setFieldValue('deleteRemindersAndRerun', false) }}
            message="You've changed the AI prompt for this automation."
            open={promptChangeDialogOpen}
            primaryText="Save"
            title="Prompt changed"
            onClickPrimary={async () => {
              setPromptChangeDialogOpen(false);
              await performSave(values);
            }}
            onClose={() => setPromptChangeDialogOpen(false)}
          >
            <Box
              sx={{ alignItems: 'center', cursor: 'pointer', display: 'flex', mt: 1 }}
              onClick={() =>
                setFieldValue('deleteRemindersAndRerun', !values.deleteRemindersAndRerun)
              }
            >
              <AlineCheckbox
                checked={values.deleteRemindersAndRerun}
                size="small"
                sx={{ p: 0, pr: 1 }}
              />
              <Typography variant="body2">
                Delete reminders generated with old prompt and re-run automation for
                previously-analyzed documents
              </Typography>
            </Box>
          </ConfirmationDialog>
        </form>
      )}
    </Formik>
  );
}

function ImprovePromptButton({
  classes,
  isPromptEmpty,
  onClick,
  state,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  isPromptEmpty: boolean;
  onClick: () => void;
  state: 'idle' | 'writing';
}) {
  const theme = useTheme();
  const isIdle = state === 'idle';

  const icon = isIdle ? (
    <SmallAiSparkleIcon fill={theme.palette.primary.main} />
  ) : (
    <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
  );

  return (
    <ButtonBase className={classes.improvePromptButton} disabled={!isIdle} onClick={onClick}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: 14,
          justifyContent: 'center',
          width: 14,
        }}
      >
        {icon}
      </Box>
      <Typography className={classes.improvePromptText} component="span">
        {isIdle ? (isPromptEmpty ? 'Generate' : 'Improve') : 'AI is writing...'}
      </Typography>
    </ButtonBase>
  );
}
